import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Receipt, Search, GraduationCap, IndianRupee, Calendar, LayoutGrid, List } from 'lucide-react';
import { toast } from 'react-toastify';
import { FeeStructure } from '../../types';
import { feeStructureApi } from '../../services/api';
import { ConfirmDialog } from '../ui';
import { sortClassNames, getClassDisplayOrder } from '../../utils/classUtils';
import { formatCurrency, formatDate } from '../../utils/helpers';

type ViewMode = 'grid' | 'list';

const typeBadgeStyles: Record<string, string> = {
  monthly: 'bg-[#16212b] text-[#bfdbfe]',
  admission: 'bg-[#173322] text-[#8ff0b8]',
  annual: 'bg-[#332711] text-[#f9daa3]'
};

const typeLabels: Record<string, string> = {
  monthly: 'Monthly',
  admission: 'Admission',
  annual: 'Annual'
};

interface FeeStructureViewProps {
  onEdit: (structure: FeeStructure) => void;
  onDelete: (structure: FeeStructure) => void;
}

const FeeStructureView: React.FC<FeeStructureViewProps> = ({ onEdit, onDelete }) => {
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterClass, setFilterClass] = useState<string>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    loadFeeStructures();
  }, []);

  const loadFeeStructures = async () => {
    try {
      setLoading(true);
      const structuresData = await feeStructureApi.getAll();
      setFeeStructures(structuresData);
    } catch (error) {
      toast.error('Failed to load fee structures');
      console.error('Error loading fee structures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStructure = (structure: FeeStructure) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Fee Structure',
      message: `Are you sure you want to delete "${structure.structureId}"?\n\nThis action cannot be undone and will remove the fee structure permanently.`,
      onConfirm: async () => {
        try {
          await feeStructureApi.delete(structure.id);
          setFeeStructures(prev => prev.filter(s => s.id !== structure.id));
          toast.success('Fee structure deleted successfully');
          onDelete(structure);
        } catch (error) {
          toast.error('Failed to delete fee structure');
          console.error('Error deleting fee structure:', error);
        }
      }
    });
  };

  const filteredStructures = feeStructures.filter(structure => {
    const matchesSearch =
      structure.structureId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.academicYear.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      !filterStatus ||
      (filterStatus === 'active' && structure.isActive) ||
      (filterStatus === 'inactive' && !structure.isActive);

    const matchesType = !filterType || structure.type === filterType;
    const matchesClass = !filterClass || structure.className === filterClass;
    const matchesYear = !filterYear || structure.academicYear === filterYear;

    return matchesSearch && matchesStatus && matchesType && matchesClass && matchesYear;
  }).sort((a, b) => getClassDisplayOrder(a.className) - getClassDisplayOrder(b.className));

  // Get unique classes and years for filters
  const uniqueClasses = sortClassNames([...new Set(feeStructures.map(s => s.className))]);
  const uniqueYears = [...new Set(feeStructures.map(s => s.academicYear))].sort();

  return (
    <div className="space-y-6">
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search structures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent w-full"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Fee Types</option>
              <option value="monthly">Monthly</option>
              <option value="admission">Admission</option>
              <option value="annual">Annual</option>
            </select>

            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Classes</option>
              {uniqueClasses.map(className => (
                <option key={className} value={className}>{className}</option>
              ))}
            </select>

            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Years</option>
              {uniqueYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[#b9ad9b]">
              {filteredStructures.length} structure{filteredStructures.length === 1 ? '' : 's'} found
            </span>
            <div className="flex items-center gap-1 bg-[#17140f] border border-[#3a3122] rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-[#e2916d] text-white' : 'text-[#8a7f6e] hover:text-[#f3ede2]'
                }`}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-[#e2916d] text-white' : 'text-[#8a7f6e] hover:text-[#f3ede2]'
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredStructures.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">
              {feeStructures.length === 0 ? 'No fee structures found' : 'No structures match your search'}
            </p>
            <p className="text-[#8a7f6e] text-sm">
              {feeStructures.length === 0 ? 'Create your first fee structure to get started' : 'Try adjusting your search criteria'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#3a3122]">
                  <th className="text-left py-3 px-6 text-xs font-medium text-[#8a7f6e] uppercase">Class</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Academic Year</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Categories</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Total</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Status</th>
                  <th className="text-right py-3 px-6 text-xs font-medium text-[#8a7f6e] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {filteredStructures.map((structure) => (
                  <tr key={structure.id} className="hover:bg-[#28221a] transition-colors">
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-[#332217] rounded-lg flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-4 h-4 text-[#f0a67d]" />
                        </div>
                        <div>
                          <p className="text-[#f3ede2] font-medium">{structure.className}</p>
                          <p className="text-[10px] font-mono text-[#5c5346]">{structure.structureId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeBadgeStyles[structure.type] || 'bg-[#28221a] text-[#b9ad9b]'}`}>
                        {typeLabels[structure.type] || structure.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#b9ad9b]">{structure.academicYear}</td>
                    <td className="py-3 px-4 text-sm text-[#b9ad9b]">
                      {structure.feeCategories.slice(0, 2).map(c => c.category).join(', ')}
                      {structure.feeCategories.length > 2 && ` +${structure.feeCategories.length - 2} more`}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-[#e2916d]">
                      {formatCurrency(structure.totalAmount)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${structure.isActive ? 'text-[#8ff0b8]' : 'text-[#8a7f6e]'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${structure.isActive ? 'bg-[#4ade80]' : 'bg-[#5c5346]'}`} />
                        {structure.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onEdit(structure)}
                          className="p-1.5 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                          title="Edit Structure"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteStructure(structure)}
                          className="p-1.5 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                          title="Delete Structure"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
            {filteredStructures.map((structure) => (
              <div
                key={structure.id}
                className="flex flex-col bg-[#17140f] rounded-xl border border-[#3a3122] hover:border-[#4a4030] transition-colors overflow-hidden"
              >
                {/* Header */}
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeBadgeStyles[structure.type] || 'bg-[#28221a] text-[#b9ad9b]'}`}>
                      {typeLabels[structure.type] || structure.type} Fee
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                        structure.isActive ? 'text-[#8ff0b8]' : 'text-[#8a7f6e]'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${structure.isActive ? 'bg-[#4ade80]' : 'bg-[#5c5346]'}`} />
                      {structure.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#332217] rounded-lg flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-5 h-5 text-[#f0a67d]" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-[#f3ede2] truncate">{structure.className}</h4>
                      <p className="text-xs text-[#8a7f6e] flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {structure.academicYear}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total amount */}
                <div className="mx-5 mb-4 bg-[#28221a] rounded-lg p-3.5 flex items-center justify-between">
                  <span className="text-xs text-[#8a7f6e] flex items-center gap-1.5">
                    <IndianRupee className="w-3.5 h-3.5" />
                    Total Amount
                  </span>
                  <span className="text-xl font-bold text-[#e2916d]">{formatCurrency(structure.totalAmount)}</span>
                </div>

                {/* Categories */}
                <div className="px-5 pb-4 flex-1 space-y-1.5">
                  {structure.feeCategories.slice(0, 4).map((category, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-[#b9ad9b] truncate">{category.category}</span>
                      <span className="text-[#f3ede2] flex-shrink-0 ml-2">{formatCurrency(category.amount)}</span>
                    </div>
                  ))}
                  {structure.feeCategories.length > 4 && (
                    <p className="text-xs text-[#8a7f6e] pt-0.5">+{structure.feeCategories.length - 4} more categories</p>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-auto px-5 py-3 border-t border-[#3a3122] flex items-center justify-between bg-[#211c15]">
                  <div className="min-w-0">
                    <p className="text-[10px] font-mono text-[#5c5346] truncate" title={structure.structureId}>
                      {structure.structureId}
                    </p>
                    <p className="text-[10px] text-[#5c5346]">Created {formatDate(structure.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onEdit(structure)}
                      className="p-1.5 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                      title="Edit Structure"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteStructure(structure)}
                      className="p-1.5 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                      title="Delete Structure"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default FeeStructureView;